// @ts-nocheck
import { useState } from 'react';
import {
  Button,
  Card,
  Code,
  Divider,
  Group,
  SimpleGrid,
  Stack,
  Text,
  Title,
} from '@/components/ui';
import {
  IconHome,
  IconUserPlus,
  IconSettings,
  IconSearch,
} from '@tabler/icons-react';
import {
  CommandPalette,
  ShortcutHelper,
  ConfirmDialog,
  CopyButton,
  CountdownTimer,
} from '@/components/advanced/utility';

export default function UtilityPage() {
  // CommandPalette state
  const [paletteOpened, setPaletteOpened] = useState(false);

  // ShortcutHelper state
  const [shortcutsOpened, setShortcutsOpened] = useState(false);

  // ConfirmDialog state
  const [confirmOpened, setConfirmOpened] = useState(false);

  // CommandPalette actions
  const commandActions = [
    {
      id: 'go-home',
      title: 'Go to Dashboard',
      description: 'Navigate to the main dashboard',
      icon: <IconHome size={18} />,
      shortcut: ['g', 'h'],
      group: 'Navigation',
      onTrigger: () => {},
    },
    {
      id: 'new-member',
      title: 'Add New Member',
      description: 'Create a new church member record',
      icon: <IconUserPlus size={18} />,
      shortcut: ['n', 'm'],
      group: 'Actions',
      onTrigger: () => {},
    },
    {
      id: 'open-settings',
      title: 'Open Settings',
      description: 'Manage your account preferences',
      icon: <IconSettings size={18} />,
      group: 'Actions',
      onTrigger: () => {},
    },
  ];

  // ShortcutHelper items
  const shortcutItems = [
    { keys: ['mod', 'k'], description: 'Open command palette', group: 'General' },
    { keys: ['?'], description: 'Show keyboard shortcuts', group: 'General' },
    { keys: ['g', 'h'], description: 'Go to dashboard', group: 'Navigation' },
    { keys: ['n', 'm'], description: 'New member', group: 'Actions' },
  ];

  return (
    <Stack gap="xl">
      <Stack gap="xs">
        <Title order={2}>Utility Components</Title>
        <Text c="dimmed" size="sm">
          Helper components for common app interactions: command palettes, keyboard
          shortcut overlays, confirmation dialogs, copy-to-clipboard, and countdown timers
        </Text>
      </Stack>

      {/* CommandPalette */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>CommandPalette</Title>
            <Text size="sm" c="dimmed">
              Searchable, keyboard-navigable command menu (⌘K style) for quick actions
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Example:</Text>
            <Group>
              <Button
                leftSection={<IconSearch size={16} />}
                onClick={() => setPaletteOpened(true)}
              >
                Open Command Palette
              </Button>
            </Group>
            <CommandPalette
              actions={commandActions}
              opened={paletteOpened}
              onClose={() => setPaletteOpened(false)}
              placeholder="Type a command or search..."
            />
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`const actions = [
  {
    id: 'go-home',
    title: 'Go to Dashboard',
    description: 'Navigate to the main dashboard',
    icon: <IconHome size={18} />,
    shortcut: ['g', 'h'],
    group: 'Navigation',
    onTrigger: () => navigate('/'),
  },
  // ... more actions
];

const [opened, setOpened] = useState(false);

<CommandPalette
  actions={actions}
  opened={opened}
  onClose={() => setOpened(false)}
  placeholder="Type a command or search..."
/>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Props:</Text>
            <Text size="xs" c="dimmed" component="div">
              • actions - Array of CommandAction items
              <br />
              • opened / onClose - Modal open state control
              <br />
              • placeholder - Search input placeholder
              <br />
              • recentActions / onRecentAction - Recent action tracking
              <br />
              • showShortcuts - Toggle keyboard hint display
            </Text>
          </Stack>
        </Stack>
      </Card>

      {/* ShortcutHelper */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>ShortcutHelper</Title>
            <Text size="sm" c="dimmed">
              Modal overlay that lists available keyboard shortcuts grouped by category
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Example:</Text>
            <Group>
              <Button variant="light" onClick={() => setShortcutsOpened(true)}>
                Show Shortcuts
              </Button>
            </Group>
            <ShortcutHelper
              shortcuts={shortcutItems}
              opened={shortcutsOpened}
              onClose={() => setShortcutsOpened(false)}
            />
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`const shortcuts = [
  { keys: ['mod', 'k'], description: 'Open command palette', group: 'General' },
  { keys: ['?'], description: 'Show keyboard shortcuts', group: 'General' },
  { keys: ['g', 'h'], description: 'Go to dashboard', group: 'Navigation' },
];

const [opened, setOpened] = useState(false);

<ShortcutHelper
  shortcuts={shortcuts}
  opened={opened}
  onClose={() => setOpened(false)}
/>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Item Interface:</Text>
            <Code block language="typescript">
{`interface ShortcutItem {
  keys: string[];      // e.g. ['mod', 'k'] -> renders ⌘ K
  description: string;
  group?: string;      // defaults to 'General'
}`}
            </Code>
          </Stack>
        </Stack>
      </Card>

      {/* ConfirmDialog */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>ConfirmDialog</Title>
            <Text size="sm" c="dimmed">
              Centered confirmation modal with typed icon variants for destructive or
              important actions
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Example:</Text>
            <Group>
              <Button color="red" onClick={() => setConfirmOpened(true)}>
                Delete Record
              </Button>
            </Group>
            <ConfirmDialog
              opened={confirmOpened}
              onClose={() => setConfirmOpened(false)}
              onConfirm={() => {}}
              type="danger"
              title="Delete this member?"
              message="This action cannot be undone. The member record will be permanently removed."
              confirmLabel="Delete"
              cancelLabel="Cancel"
            />
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`const [opened, setOpened] = useState(false);

<ConfirmDialog
  opened={opened}
  onClose={() => setOpened(false)}
  onConfirm={() => handleDelete()}
  type="danger"
  title="Delete this member?"
  message="This action cannot be undone."
  confirmLabel="Delete"
  cancelLabel="Cancel"
/>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Props:</Text>
            <Text size="xs" c="dimmed" component="div">
              • type - 'danger' | 'warning' | 'info' | 'success' | 'question'
              <br />
              • title / message - Dialog heading and body
              <br />
              • confirmLabel / cancelLabel - Button text
              <br />
              • loading - Shows spinner on confirm button
              <br />
              • onConfirm - May return a Promise (awaited before close)
            </Text>
          </Stack>
        </Stack>
      </Card>

      {/* CopyButton */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>CopyButton</Title>
            <Text size="sm" c="dimmed">
              One-click copy-to-clipboard action icon with a "Copied!" tooltip confirmation
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Example:</Text>
            <Group gap="md">
              <Group gap="xs">
                <Code>member-id: 9F3A-22B1</Code>
                <CopyButton value="9F3A-22B1" />
              </Group>
              <Group gap="xs">
                <Code>https://ccms.app/invite/abc123</Code>
                <CopyButton value="https://ccms.app/invite/abc123" variant="light" />
              </Group>
            </Group>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`<CopyButton value="9F3A-22B1" />

// With a different button variant
<CopyButton
  value="https://ccms.app/invite/abc123"
  variant="light"
  label="Copy link"
  copiedLabel="Link copied!"
/>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Props:</Text>
            <Text size="xs" c="dimmed" component="div">
              • value - String to copy to clipboard (required)
              <br />
              • label / copiedLabel - Tooltip text before/after copy
              <br />
              • timeout - Ms to show copied state (default 2000)
              <br />
              • variant / size - ActionIcon styling
              <br />
              • children - Render-prop for fully custom trigger
            </Text>
          </Stack>
        </Stack>
      </Card>

      {/* CountdownTimer */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>CountdownTimer</Title>
            <Text size="sm" c="dimmed">
              Ring-progress countdown timer with start, pause, and reset controls
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Example:</Text>
            <Group justify="center" gap="xl">
              <CountdownTimer
                duration={60}
                label="Session timeout"
                format="mm:ss"
                onComplete={() => {}}
              />
              <CountdownTimer
                duration={30}
                size="sm"
                label="OTP expires in"
                format="seconds"
                onComplete={() => {}}
              />
            </Group>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`<CountdownTimer
  duration={60}            // seconds
  label="Session timeout"
  format="mm:ss"
  onComplete={() => handleTimeout()}
/>

<CountdownTimer
  duration={30}
  size="sm"
  format="seconds"
  autoStart
  onTick={(remaining) => console.log(remaining)}
/>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Props:</Text>
            <Text size="xs" c="dimmed" component="div">
              • duration - Countdown length in seconds (required)
              <br />
              • format - 'mm:ss' | 'hh:mm:ss' | 'seconds'
              <br />
              • autoStart - Begin counting on mount
              <br />
              • showControls / showProgress - Toggle buttons and ring
              <br />
              • onComplete / onTick - Lifecycle callbacks
            </Text>
          </Stack>
        </Stack>
      </Card>

      {/* Use Cases */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Title order={4}>Common Use Cases</Title>

          <SimpleGrid cols={{ base: 1, md: 3 }} spacing="md">
            <Stack gap="xs">
              <Text size="sm" fw={500}>Power-User Navigation</Text>
              <Text size="xs" c="dimmed">
                Pair CommandPalette and ShortcutHelper so users can jump anywhere and
                discover keyboard shortcuts without leaving the keyboard.
              </Text>
            </Stack>

            <Stack gap="xs">
              <Text size="sm" fw={500}>Safe Destructive Actions</Text>
              <Text size="xs" c="dimmed">
                Gate deletions and irreversible operations behind a ConfirmDialog with the
                danger variant to prevent accidental data loss.
              </Text>
            </Stack>

            <Stack gap="xs">
              <Text size="sm" fw={500}>Sessions & One-Time Codes</Text>
              <Text size="xs" c="dimmed">
                Use CountdownTimer for session timeouts and OTP expiry, and CopyButton to
                let users grab IDs, tokens, and invite links instantly.
              </Text>
            </Stack>
          </SimpleGrid>

          <Divider />

          <Text size="xs" c="dimmed">
            All utility components honor the app's gradient theme and accessibility
            preferences (line spacing, letter spacing) out of the box.
          </Text>
        </Stack>
      </Card>
    </Stack>
  );
}
